const finiteNonNegative = (value) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized >= 0 ? normalized : 0;
};

export const formatAudioTime = (seconds) => {
  const normalized = finiteNonNegative(seconds);
  const minutes = Math.floor(normalized / 60);
  const remaining = (normalized % 60).toFixed(1).padStart(4, '0');
  return `${String(minutes).padStart(2, '0')}:${remaining}`;
};

export const clampAudioClipRange = ({
  durationSeconds,
  maxDurationSeconds,
  start = 0,
  end,
} = {}) => {
  const duration = finiteNonNegative(durationSeconds);
  const maxDuration = Math.min(
    duration,
    Math.max(0, finiteNonNegative(maxDurationSeconds)),
  );
  if (duration === 0 || maxDuration === 0) return { start: 0, end: 0 };

  const normalizedStart = Math.min(finiteNonNegative(start), duration);
  const normalizedEnd = end === undefined
    ? Math.min(duration, normalizedStart + maxDuration)
    : Math.min(Math.max(finiteNonNegative(end), normalizedStart), duration);
  if (normalizedEnd - normalizedStart <= maxDuration) {
    return { start: normalizedStart, end: normalizedEnd };
  }
  return {
    start: normalizedStart,
    end: Math.min(duration, normalizedStart + maxDuration),
  };
};

const writeString = (view, offset, value) => {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
};

export const encodeAudioBufferAsWav = (
  audioBuffer,
  { startSeconds = 0, endSeconds = audioBuffer.duration } = {},
) => {
  const sampleRate = audioBuffer.sampleRate;
  const channelCount = audioBuffer.numberOfChannels;
  const startFrame = Math.max(
    0,
    Math.min(audioBuffer.length, Math.floor(startSeconds * sampleRate)),
  );
  const endFrame = Math.max(
    startFrame,
    Math.min(audioBuffer.length, Math.ceil(endSeconds * sampleRate)),
  );
  const frameCount = endFrame - startFrame;
  const bytesPerSample = 2;
  const dataLength = frameCount * channelCount * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channelCount * bytesPerSample, true);
  view.setUint16(32, channelCount * bytesPerSample, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  const channels = Array.from(
    { length: channelCount },
    (_, index) => audioBuffer.getChannelData(index),
  );
  let offset = 44;
  for (let frame = startFrame; frame < endFrame; frame += 1) {
    for (let channel = 0; channel < channelCount; channel += 1) {
      const sample = Math.max(-1, Math.min(1, channels[channel][frame]));
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true,
      );
      offset += bytesPerSample;
    }
  }
  return new Blob([buffer], { type: 'audio/wav' });
};

const createAudioContext = () => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) throw new Error('当前浏览器不支持音频处理');
  return new AudioContextClass();
};

export const decodeAudioFile = async (file) => {
  const audioContext = createAudioContext();
  try {
    const audioBuffer = await audioContext.decodeAudioData(
      await file.arrayBuffer(),
    );
    return {
      audioBuffer,
      durationSeconds: audioBuffer.duration,
    };
  } finally {
    await audioContext.close();
  }
};

export const trimAudioFileToWav = async ({
  file,
  startSeconds,
  endSeconds,
}) => {
  const audioContext = createAudioContext();
  try {
    const audioBuffer = await audioContext.decodeAudioData(
      await file.arrayBuffer(),
    );
    return createTrimmedWavFile({
      audioBuffer,
      fileName: file.name,
      startSeconds,
      endSeconds,
    });
  } finally {
    await audioContext.close();
  }
};

export const createTrimmedWavFile = ({
  audioBuffer,
  fileName,
  startSeconds,
  endSeconds,
}) => {
  const blob = encodeAudioBufferAsWav(audioBuffer, {
    startSeconds,
    endSeconds,
  });
  const baseName = String(fileName || 'audio').replace(/\.[^.]+$/, '');
  return {
    file: new File([blob], `${baseName}_trimmed.wav`, {
      type: 'audio/wav',
    }),
    durationSeconds: Math.max(0, endSeconds - startSeconds),
  };
};
